import { computed, ref, unref } from 'vue';
import { getPlatformFromMediaSource } from '@/stores/campaigns';
import { logger } from '@/utils/logger';
import {
  CONTENT_TAG_ADDED_METHOD,
  CONTENT_TAG_MEDIA_ACTIONS,
  CONTENT_TAGS_EVENT_NAMES,
} from '@/app/settings/constants';
import { useContentTagsStore } from '@/stores/content-tags';
import { useTrackingStore } from '@/stores/tracking';
import { useAuthStore } from '@/stores/auth';
import {
  canAccessContentTags,
  NO_ACCESS_TO_CONTENT_TAG_TOOLTIP,
} from '@/components/ContentTags/content-tag-utils';
import { useToast } from '@dashhudson/dashing-ui';

export function useContentTags(selectedMedia) {
  const contentTagsStore = useContentTagsStore();
  const trackingStore = useTrackingStore();
  const authStore = useAuthStore();
  const { addToast } = useToast({
    life: 5000,
  });

  const saving = ref(false);
  const TAG_CHUNK_SIZE = 20;
  const MEDIA_CHUNK_SIZE = 100;

  const selectedMediaIds = computed(
    () =>
      unref(selectedMedia)?.map(
        (mediaItem) =>
          /*
        /media/v1 stores the Brand Media ID in the id field
        /media/v2 stores the Brand Media ID in the brand_media_id field
        */
          mediaItem.brandMediaId ?? mediaItem.brand_media_id ?? mediaItem.id,
      ) ?? [],
  );

  function sendAddedContentTagsEvent(contentTagIds) {
    const contentTags =
      contentTagsStore.organizationContentTags
        ?.filter((organizationContentTag) =>
          contentTagIds.some((contentTagId) => organizationContentTag.id === contentTagId),
        )
        ?.map((contentTag) => ({
          label: contentTag.name,
          value: contentTag.id,
          brandIds: contentTag.brandIds,
        })) ?? [];

    const brandsAssociatedToTags = {};
    contentTags.forEach((tag) => {
      brandsAssociatedToTags[tag.label] = authStore.getBrandNames(tag.brandIds);
    });

    trackingStore.track(CONTENT_TAGS_EVENT_NAMES.CONTENT_TAG_MEDIA_ACTION, {
      action: CONTENT_TAG_MEDIA_ACTIONS.ADD,
      tagNames: contentTags.map((tag) => tag.label),
      tagIDs: contentTags.map((tag) => tag.value),
      tagAddedMethod: CONTENT_TAG_ADDED_METHOD.MEDIA_MULTI_SELECT,
      channels: Array.from(
        new Set(
          unref(selectedMedia).map((media) =>
            getPlatformFromMediaSource(media?.source ?? 'UPLOAD'),
          ),
        ),
      ),
      totalMedia: unref(selectedMedia).length,
      brandsAssociatedToTags,
    });
  }
  function assignContentTags(contentTagIds, brandMediaIds) {
    return contentTagsStore.associateContentTagsToBrandMedia({
      contentTagIds,
      brandMediaIds,
    });
  }
  async function onSaveClick(contentTagIds) {
    if (saving.value) return;
    const brandMediaIds = selectedMediaIds.value;

    if (brandMediaIds?.length < 1 || contentTagIds?.length < 1) {
      return;
    }

    saving.value = true;
    const assignContentTagPromises = [];
    for (let i = 0; i < contentTagIds.length; i += TAG_CHUNK_SIZE) {
      const chunkOfContentTagIds = contentTagIds.slice(i, i + TAG_CHUNK_SIZE);
      for (let j = 0; j < brandMediaIds.length; j += MEDIA_CHUNK_SIZE) {
        const chunkOfBrandMediaIds = brandMediaIds.slice(j, j + MEDIA_CHUNK_SIZE);
        assignContentTagPromises.push(
          assignContentTags(chunkOfContentTagIds, chunkOfBrandMediaIds),
        );
      }
    }
    try {
      await Promise.all(assignContentTagPromises);
      sendAddedContentTagsEvent(contentTagIds);
      addToast({
        severity: 'success',
        message: 'Your content tags have been added!',
      });
    } catch (error) {
      logger.error(`Assign content tags error`, {}, error);
      addToast({
        severity: 'error',
        message: 'An error occurred adding content tags to media.',
      });
    } finally {
      saving.value = false;
    }
  }

  const addContentTagsTooltip = computed(() => {
    if (canAccessContentTags()) {
      return 'Add Content Tags';
    }

    return NO_ACCESS_TO_CONTENT_TAG_TOOLTIP;
  });

  const availableContentTags = computed(
    () =>
      contentTagsStore.organizationContentTags
        ?.filter((organizationContentTag) =>
          organizationContentTag.brandIds.some(
            (contentTagBrandId) => authStore.currentBrand.id === contentTagBrandId,
          ),
        )
        ?.map((contentTag) => ({
          label: contentTag.name,
          value: contentTag.id,
        })) ?? [],
  );

  return {
    saving,
    onSaveClick,
    availableContentTags,
    addContentTagsTooltip,
  };
}
